import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface CompanyUser {
  id: string;
  company_id: string;
  user_id: string;
  role_id: string;
  first_name: string;
  last_name: string;
  display_name: string | null;
  email: string;
  phone: string | null;
  title: string | null;
  profile_photo_url: string | null;
  profile_photo_path: string | null;
  status: string;
  invited_by: string | null;
  invited_at: string | null;
  activated_at: string | null;
  last_login: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
  roles?: { name: string; color: string; base_level: number; permissions: Record<string, boolean> } | null;
}

export const useCompanyUsers = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["company_users", user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("user_profiles")
        .select("*, roles(name, color, base_level, permissions)")
        .not("company_id", "is", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      // Map to CompanyUser shape for backward compat
      return ((data ?? []) as any[]).map((row) => ({
        ...row,
        first_name: row.first_name ?? "",
        last_name: row.last_name ?? "",
        email: row.email ?? "",
        display_name: row.display_name ?? `${row.first_name ?? ""} ${row.last_name ?? ""}`.trim(),
      })) as CompanyUser[];
    },
    enabled: !!user,
  });
};

export const useInviteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      company_id: string;
      role_id: string;
      first_name: string;
      last_name: string;
      email: string;
      phone?: string;
      title?: string;
      project_ids?: string[];
    }) => {
      // Use the invite-user edge function instead of direct admin calls
      const { data: result, error } = await supabase.functions.invoke("invite-user", {
        body: {
          email: payload.email,
          first_name: payload.first_name,
          last_name: payload.last_name,
          role_id: payload.role_id,
          invite_type: "company_user",
          company_id: payload.company_id,
          project_ids: payload.project_ids || [],
          phone: payload.phone || null,
          title: payload.title || null,
        },
      });
      if (error) throw error;
      if (result?.error) throw new Error(result.error);
      return result;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company_users"] }),
  });
};

export const useUpdateCompanyUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { id: string } & Partial<CompanyUser>) => {
      const { id, roles, ...rest } = payload;
      const { data, error } = await supabase
        .from("user_profiles")
        .update(rest as any)
        .eq("id", id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["company_users"] }),
  });
};
